import { useEffect, useState } from "react";
import { api, unwrap } from "../api/client";
import type { GwEvent, StateMessage } from "../api/types";
import { InputForm } from "../components/InputForm";
import { HistoryPanel, RecordingsPanel } from "../components/InputPanels";
import { Sparkline } from "../components/Sparkline";
import { InputHealthPill, OutputStatePill } from "../components/status";
import { Button, ConfirmDialog, CopyField, EmptyState, PageHeader, Panel, Stat, Tag, useToast } from "../components/ui";
import { WhepPlayer } from "../components/WhepPlayer";
import { useCan } from "../lib/auth";
import { formatBitrate, formatNumber, formatTime, PROTOCOL_LABEL } from "../lib/format";
import { rich, t } from "../lib/i18n";
import { mergeEvents, useLive } from "../lib/live";
import { href } from "../lib/router";
import { EventRow } from "./Journal";

export function InputDetail({ state, id }: { state: StateMessage; id: string }) {
  const { history, events } = useLive();
  const toast = useToast();
  const can = useCan();
  const [editing, setEditing] = useState(false);
  const [kicking, setKicking] = useState(false);
  const [past, setPast] = useState<GwEvent[]>([]);
  const input = state.inputs.find((i) => i.id === id);
  const source = `input:${id}`;

  useEffect(() => {
    unwrap(api.GET("/api/events", { params: { query: { source, limit: 50 } } })).then(setPast).catch(() => setPast([]));
  }, [source]);

  if (!input) {
    return <EmptyState title={t("Entrée « {id} » introuvable", { id })}
      action={<a className="text-accent hover:underline" href={href({ page: "inputs" })}>{t("Retour aux entrées")}</a>} />;
  }

  const live = input.live;
  const srt = live.srt;
  const fed = state.outputs.filter((o) => o.input_id === id);
  const journal = mergeEvents(past, events.filter((e) => e.source === source).reverse()).slice(0, 30);

  const kick = async () => {
    try {
      await unwrap(api.POST("/api/inputs/{input_id}/kick", { params: { path: { input_id: id } } }));
      toast(t("Encodeur déconnecté. Il peut se reconnecter."), "warn");
    } catch (e) {
      toast((e as Error).message, "err");
    }
    setKicking(false);
  };

  return (
    <div className="flex flex-col gap-5">
      <div>
        <a href={href({ page: "inputs" })} className="text-[13px] text-muted hover:text-ink">← {t("Entrées")}</a>
      </div>
      <PageHeader title={input.name}
        subtitle={<span className="inline-flex flex-wrap items-center gap-2">
          <InputHealthPill input={input} alarms={state.alarms} />
          <Tag>{PROTOCOL_LABEL[input.protocol]} · {input.mode}</Tag>
          <span className="font-mono text-[12px] text-faint">{input.id}</span>
        </span>}
        actions={<>
          {can("operator") && input.mode === "push" && live.online && <Button onClick={() => setKicking(true)}>{t("Déconnecter l'encodeur")}</Button>}
          {can("operator") && <Button onClick={() => setEditing(true)}>{t("Modifier")}</Button>}
        </>} />

      <div className="grid gap-5 lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
        <WhepPlayer inputId={input.id} online={live.online} video={(live.tracks ?? []).some((t) => /\d+x\d+/.test(t)) || !live.tracks?.length}
          interlaced={live.content?.interlaced === true && input.deinterlace === "off"}
          bFrames={live.content?.b_frames === true} />

        <Panel title={t("Signal")}>
          <div className="flex flex-col gap-4 p-4">
            <div className="flex items-end justify-between gap-4">
              <Stat label={t("Débit entrant")} value={formatBitrate(live.bitrate_in_kbps)} />
              <Sparkline values={history[id] ?? []} className="h-12 w-44" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Stat label={t("Lecteurs")} value={live.readers ?? 0}
                sub={Object.entries(live.readers_by_type ?? {}).map(([k, v]) => `${k.replace(/Session|Conn/, "")} ${v}`).join(" · ") || undefined} />
              <Stat label={t("Débit sortant")} value={formatBitrate(live.bitrate_out_kbps)} />
              <Stat label={t("Encodeur")} value={<span className="font-mono text-[13px]">{live.publisher ?? "–"}</span>} />
              <Stat label={t("Images en erreur")} value={live.frames_in_error ?? 0} tone={(live.frames_in_error ?? 0) > 0 ? "warn" : undefined} />
            </div>
            {live.online && (
              <div className="flex flex-wrap gap-1">{live.tracks.map((t) => <Tag key={t} caps={false}>{t}</Tag>)}</div>
            )}
          </div>
          {srt && (
            <div className="grid grid-cols-2 gap-4 border-t border-line p-4 sm:grid-cols-3">
              <Stat label="RTT" value={formatNumber(srt.rtt_ms, "ms")} />
              <Stat label={t("Pertes (1 s)")} value={formatNumber(srt.loss_pct, "%")} tone={srt.loss_pct > 0 ? "warn" : "ok"} />
              <Stat label={t("Capacité du lien")} value={formatNumber(srt.link_mbps, "Mb/s")} />
              <Stat label={t("Paquets perdus")} value={srt.loss_total} sub={t("{retrans} retransmis · {drop} abandonnés", { retrans: srt.retrans_total, drop: srt.drop_total })} />
              <Stat label={t("Latence")} value={formatNumber(srt.latency_ms, "ms")} />
              <Stat label={t("Tampon")} value={formatNumber(srt.buffer_ms, "ms")} />
            </div>
          )}
        </Panel>
      </div>

      <div className="grid gap-5 lg:grid-cols-2">
        <Panel title={t("Adresses")}>
          <div className="flex flex-col gap-3 p-4">
            {input.push_url ? (
              <div className="flex flex-col gap-1.5">
                <span className="text-[13px] text-muted">{t("À saisir dans l'encodeur :")}</span>
                <CopyField value={input.push_url} />
              </div>
            ) : input.mode === "push" ? (
              <p className="text-[13px] text-faint">{t("L'URL de l'encodeur (avec sa clé) est visible par les opérateurs.")}</p>
            ) : (
              <div className="flex flex-col gap-1.5">
                <span className="text-[13px] text-muted">{t("Source récupérée par la gateway :")}</span>
                <CopyField value={input.source_url ?? ""} />
              </div>
            )}
            <span className="mt-2 text-[13px] text-muted">{t("Lecture (VLC, ffplay, autre serveur) :")}</span>
            {(["srt", "rtsp", "rtmp", "hls"] as const).map((k) => <CopyField key={k} label={k} value={input.read_urls[k]} />)}
          </div>
        </Panel>

        <Panel title={t("Sorties alimentées ({n})", { n: fed.length })}>
          {fed.length === 0 ? (
            <p className="p-4 text-muted">{t("Aucune sortie n'utilise cette entrée.")}</p>
          ) : (
            <ul className="divide-y divide-line">
              {fed.map((o) => (
                <li key={o.id} className="flex items-center gap-3 px-4 py-2">
                  <OutputStatePill state={o.status.state} />
                  <a href={href({ page: "outputs" })} className="min-w-0 flex-1 truncate hover:text-accent">{o.name}</a>
                  <span className="num text-[13px]">{formatBitrate(o.status.bitrate_kbps)}</span>
                </li>
              ))}
            </ul>
          )}
        </Panel>
      </div>

      <HistoryPanel inputId={input.id} />
      <RecordingsPanel inputId={input.id} record={input.record} />

      <Panel title={t("Journal de l'entrée")}>
        {journal.length === 0 ? <p className="p-4 text-muted">{t("Aucun événement.")}</p> : (
          <table className="w-full text-left"><tbody className="divide-y divide-line">
            {journal.map((e) => <EventRow key={e.id} event={e} state={state} hideSource />)}
          </tbody></table>
        )}
        {live.since && <p className="border-t border-line px-4 py-2 text-[12px] text-faint">{t("En ligne depuis {time}", { time: formatTime(Date.parse(live.since) / 1000) })}</p>}
      </Panel>

      {editing && <InputForm input={input} onClose={() => setEditing(false)}
        onSaved={(s) => { toast(t("Entrée « {name} » modifiée", { name: s.name })); setEditing(false); }} />}
      <ConfirmDialog open={kicking} title={t("Déconnecter l'encodeur ?")} confirmLabel={t("Déconnecter")}
        onCancel={() => setKicking(false)} onConfirm={kick}>
        {rich("L'encodeur <ip>{publisher}</ip> sera coupé. S'il est réglé pour se reconnecter, il reviendra aussitôt ; les sorties passeront en attente entre-temps.", { ip: (s) => <span className="font-mono">{s}</span> }, { publisher: live.publisher ?? "" })}
      </ConfirmDialog>
    </div>
  );
}
